import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Eye, AlertTriangle, FileText, User, Calendar, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTickets } from '../../contexts/TicketContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { safeParseArray } from '../../utils/storageUtils';
import { supabase } from '../../lib/supabase';

const StaffTicketVerification = () => {
    const { user } = useAuth();
    const [searchProtocol, setSearchProtocol] = useState('');
    const [foundTicket, setFoundTicket] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchProtocol.trim()) return;

        setHasSearched(true);
        setIsSearching(true);
        setFoundTicket(null);

        try {
            const term = searchProtocol.trim().toUpperCase();

            // Direct Search in Supabase (Accessing ANY ticket as requested)
            const { data, error } = await supabase
                .from('tickets')
                .select('*')
                .or(`id.eq.${term},custom_id.eq.${term},display_id.eq.${term}`)
                .maybeSingle();

            if (error) throw error;

            if (data) {
                // Map snake_case to camelCase for UI compatibility
                const ticket = {
                    ...data,
                    id: data.id,
                    userDiscordId: data.user_discord_id,
                    authorName: data.user_name,
                    createdAt: data.created_at,
                    status: data.status,
                    category: data.category,
                    title: data.title
                };

                setFoundTicket(ticket);
                // Log action (Simulation as per request "mensagem que aquela ação será registra na logs")
                console.log(`[AUDIT] Staff ${user?.name} (${user?.discordId}) verified ticket ${ticket.id}`);
                toast.info('Consulta registrada nos logs de segurança.');
            } else {
                toast.error('Ticket/Protocolo não encontrado.');
            }
        } catch (error) {
            console.error('Error searching ticket', error);
            toast.error('Erro ao buscar ticket.');
        } finally {
            setIsSearching(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'aberto': return 'bg-green-500/10 text-green-500 border-green-500/20';
            case 'em_andamento': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'fechado': return 'bg-red-500/10 text-red-500 border-red-500/20';
            default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'aberto': return 'Aberto';
            case 'em_andamento': return 'Em Andamento';
            case 'fechado': return 'Fechado';
            default: return status;
        }
    };

    return (
        <Card className="bg-black/40 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden min-h-[500px]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-[100px] -z-10" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full blur-[100px] -z-10" />

            <CardHeader className="border-b border-white/5 pb-6">
                <div>
                    <CardTitle className="text-2xl font-bold text-white flex items-center gap-3">
                        <Search className="h-6 w-6 text-emerald-400" />
                        Verificação de Tickets
                    </CardTitle>
                    <CardDescription className="text-gray-400 mt-1">
                        Consulte tickets pelo protocolo ou ID. Esta ação é monitorada.
                    </CardDescription>
                </div>
            </CardHeader>

            <CardContent className="p-6 space-y-8">
                {/* Search Form */}
                <div className="max-w-xl mx-auto space-y-4">
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                        <div>
                            <h4 className="text-sm font-semibold text-amber-500">Atenção Staff</h4>
                            <div className="text-xs text-amber-400/80 mt-2 space-y-2">
                                <p>
                                    Atenção: Esta ferramenta permite a visualização de tickets independentemente das permissões padrão. Toda consulta realizada será registrada no histórico de atividades do usuário e nos logs do ticket.
                                </p>
                                <p>
                                    O acesso deve ocorrer exclusivamente mediante necessidade operacional, permissão ou autorização prévia.
                                </p>
                                <p>
                                    O uso indevido ou sem justificativa poderá resultar em medidas administrativas, conforme as normas internas.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSearch} className="flex gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <Input
                                placeholder="Digite o Protocolo ou ID do Ticket..."
                                value={searchProtocol}
                                onChange={(e) => setSearchProtocol(e.target.value)}
                                className="pl-12 bg-white/5 border-white/10 text-white h-12 rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20 transition-all placeholder:text-gray-600"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={isSearching}
                            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 border-0 rounded-xl px-6 h-12 transition-all hover:scale-105 active:scale-95 flex items-center gap-2"
                        >
                            {isSearching ? <Clock className="h-4 w-4 animate-spin" /> : 'Pesquisar'}
                        </Button>
                    </form>
                </div>

                {/* Result */}
                {foundTicket ? (
                    <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-emerald-500/30 transition-all group">
                            <div className="p-6 space-y-6">
                                {/* Header Info */}
                                <div className="flex items-start justify-between gap-4">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-mono text-gray-500 bg-white/5 px-2 py-1 rounded">
                                                #{foundTicket.id}
                                            </span>
                                            <Badge className={`${getStatusColor(foundTicket.status)} border px-2.5 py-0.5 capitalize`}>
                                                {getStatusLabel(foundTicket.status)}
                                            </Badge>
                                        </div>
                                        <h3 className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                                            {foundTicket.title || 'Sem título'}
                                        </h3>
                                    </div>
                                    <Link to={`/staff/tickets/${foundTicket.id}`}>
                                        <Button className="bg-white/10 hover:bg-emerald-500 hover:text-white text-emerald-400 border border-emerald-500/30 hover:border-emerald-500/0 transition-all rounded-lg gap-2">
                                            <Eye className="h-4 w-4" />
                                            Ver Ticket
                                        </Button>
                                    </Link>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Author Info */}
                                    <div className="bg-black/20 rounded-xl p-4 space-y-3">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider font-semibold">
                                            <User className="h-4 w-4" />
                                            Autor
                                        </div>
                                        <div>
                                            <div className="text-white font-medium">{foundTicket.authorName || 'Desconhecido'}</div>
                                            <div className="text-sm text-gray-500">ID: {foundTicket.userDiscordId}</div>
                                        </div>
                                    </div>

                                    {/* Date Info */}
                                    <div className="bg-black/20 rounded-xl p-4 space-y-3">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider font-semibold">
                                            <Calendar className="h-4 w-4" />
                                            Data de Criação
                                        </div>
                                        <div>
                                            <div className="text-white font-medium">
                                                {new Date(foundTicket.createdAt).toLocaleDateString('pt-BR')}
                                            </div>
                                            <div className="text-sm text-gray-500 flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {new Date(foundTicket.createdAt).toLocaleTimeString('pt-BR')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Category Info */}
                                    <div className="bg-black/20 rounded-xl p-4 space-y-3 sm:col-span-2">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider font-semibold">
                                            <FileText className="h-4 w-4" />
                                            Categoria
                                        </div>
                                        <div className="text-white font-medium capitalize">
                                            {foundTicket.category ? foundTicket.category.replace(/_/g, ' ') : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Warning */}
                            <div className="bg-emerald-500/5 border-t border-emerald-500/10 p-4 text-center">
                                <p className="text-xs text-emerald-400/70 flex items-center justify-center gap-2">
                                    <Eye className="h-3 w-3" />
                                    Ao clicar em "Ver Ticket", você acessará o conteúdo completo e isso será logado.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : hasSearched && (
                    <div className="text-center py-12 animate-in fade-in zoom-in duration-300">
                        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-600">
                            <Search className="h-8 w-8" />
                        </div>
                        <h3 className="text-lg font-medium text-white mb-1">Nenhum ticket encontrado</h3>
                        <p className="text-gray-500">Verifique o ID ou Protocolo e tente novamente.</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default StaffTicketVerification;
